import { z } from '@kc3/validation';
export const RoutingStrategy = {
    ROUND_ROBIN: 'round_robin',
    LEAST_RECENT: 'least_recent',
    SKILLS_BASED: 'skills_based',
    LONGEST_IDLE: 'longest_idle',
    RANDOM: 'random',
};
export const RoutingStrategySchema = z.enum([
    'round_robin',
    'least_recent',
    'skills_based',
    'longest_idle',
    'random',
]);
export const DialerType = {
    PREVIEW: 'preview',
    PROGRESSIVE: 'progressive',
    POWER: 'power',
    PREDICTIVE: 'predictive',
};
export const DialerTypeSchema = z.enum([
    'preview',
    'progressive',
    'power',
    'predictive',
]);
/**
 * Phase 5 Task 2 — uppercase DialerType used by `dialer.attempt.started`
 * (and Task 3+ siblings). Distinct from {@link DialerTypeSchema} above
 * which is locked to Phase 1's lowercase wire format and can't be
 * widened without breaking persisted `dialer.originate` events.
 *
 * Mirrors `@kc3/types.DialerType`; kept here so the schema graph
 * stays a self-contained Zod tree.
 */
export const DialerKindSchema = z.enum([
    'MANUAL',
    'PREVIEW',
    'PROGRESSIVE',
    'POWER',
    'PREDICTIVE',
    'ROBO',
    'AMD',
]);
/**
 * Disposition of a completed dialer attempt — Phase 5 Task 2 baseline
 * (Task 3+ may extend with `MACHINE`, `TRANSFER`, etc).
 */
export const DialerDispositionSchema = z.enum([
    'ANSWERED',
    'NO_ANSWER',
    'BUSY',
    'FAILED',
    'VOICEMAIL',
    'ABANDONED',
    'DNC',
    'CONSENT',
]);
export const CallOutcome = {
    ANSWERED: 'answered',
    NO_ANSWER: 'no_answer',
    BUSY: 'busy',
    FAILED: 'failed',
    VOICEMAIL: 'voicemail',
    ABANDONED: 'abandoned',
};
export const CallOutcomeSchema = z.enum([
    'answered',
    'no_answer',
    'busy',
    'failed',
    'voicemail',
    'abandoned',
]);
/**
 * MUST stay in step with `AgentStatus` in `@kc3/types` (`packages/types/src/agent.ts`)
 * and with `AGENT_TRANSITIONS`. This is a second declaration of the same union
 * — contracts cannot import from types without a dependency edge — so the
 * compiler only catches drift where a value flows between the two. It caught
 * exactly that when the reservation states were added: `agent-sync.ts` builds
 * an `AgentStatusChangedEvent` from a `@kc3/types` status, and this enum was
 * narrower.
 *
 * If you add a state in one place and not the other, an agent can reach a
 * status that cannot be published — which silently drops the event that tells
 * the rest of the platform the agent moved.
 */
export const AgentStatus = {
    OFFLINE: 'OFFLINE',
    /** SIP-registered, not yet logged in to the ACD. */
    REGISTERED: 'REGISTERED',
    /** ≡ AVAILABLE */
    IDLE: 'IDLE',
    /** Reservation CAS in flight. */
    RESERVING: 'RESERVING',
    /** Reservation held; not yet dialled. */
    RESERVED: 'RESERVED',
    /** ARI originate issued; no SIP response yet. */
    INVITING: 'INVITING',
    RINGING: 'RINGING',
    /** 200 OK received; bridge not yet established. */
    ANSWERED: 'ANSWERED',
    /** ≡ CONNECTED */
    IN_CALL: 'IN_CALL',
    PAUSED: 'PAUSED',
    ON_BREAK: 'ON_BREAK',
    /** ≡ WRAPUP */
    ACW: 'ACW',
    AVAILABLE_ON_DEMAND: 'AVAILABLE_ON_DEMAND',
};
export const AgentStatusSchema = z.enum([
    'OFFLINE',
    'REGISTERED',
    'IDLE',
    'RESERVING',
    'RESERVED',
    'INVITING',
    'RINGING',
    'ANSWERED',
    'IN_CALL',
    'PAUSED',
    'ON_BREAK',
    'ACW',
    'AVAILABLE_ON_DEMAND',
]);
export const IncidentSeverity = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
};
export const IncidentSeveritySchema = z.enum([
    'low',
    'medium',
    'high',
    'critical',
]);
export const ContentType = {
    NEWS: 'news',
    KNOWLEDGEBASE: 'knowledgebase',
    FAQ: 'faq',
    ANNOUNCEMENT: 'announcement',
};
export const ContentTypeSchema = z.enum([
    'news',
    'knowledgebase',
    'faq',
    'announcement',
]);
export const PortalVisibility = {
    EVERYONE: 'everyone',
    LOGGED_IN_USER: 'logged_in_user',
};
export const PortalVisibilitySchema = z.enum([
    'everyone',
    'logged_in_user',
]);
//# sourceMappingURL=index.js.map